<template>
	<section
		v-editable="blok"
		class="container flex items-start gap-12 flex-col md:flex-row"
		:aria-label="blok.title ?? 'Více informací'"
	>
		<div
			ref="contentColumnRef"
			class="space-y-2 flex-1"
			:style="isCollapsed ? {'--content-height': `${MAX_CONTENT_HEIGHT}px`} : {}"
		>
			<h2 class="font-secondary text-xl sm:typo-h2">
				{{ blok.title }}
			</h2>
			<RichText
				v-if="blok.subtitle"
				class="font-primary typo-body-caption !text-base text-heading-primary rich-text-editor--sm leading-5"
				:data="blok.subtitle"
			/>

			<div
				ref="contentWrapperRef"
				class="relative transition-all duration-700 ease-out"
				:class="{'overflow-hidden max-h-[var(--content-height)]': isCollapsed}"
			>
				<RichText
					class="font-primary typo-body-caption !text-base text-heading-primary rich-text-editor--sm leading-5 word-break-word"
					:data="blok.content"
				/>

				<Transition
					name="fade"
					mode="out-in"
				>
					<div
						v-if="isCollapsed"
						key="preview-overlay"
						class="
							absolute bottom-0 w-full bg-white flex justify-center items-end
							before:absolute before:inset-x-0 before:bottom-full before:h-20 before:bg-gradient-to-t before:from-background-primary before:to-[rgba(255,255,255,0)] before:z-0
						"
					/>
				</Transition>
			</div>
			<div
				v-if="isOverflowing"
				class="flex justify-center relative after:border-[.0625rem] after:border-neutral-500 after:absolute after:inset-x-0 after:bottom-2.5 after:z-1"
			>
				<button
					type="button"
					class="flex items-center gap-2 px-2 bg-white z-10 relative"
					:aria-expanded="!isCollapsed"
					@click="toggleExpanded()"
				>
					{{ isCollapsed ? 'Zobrazit více' : 'Zobrazit méně' }}
					<NuxtIcon
						name="chevron-down"
						class="text-2xl text-icon-primary transition-transform duration-200"
						:class="{'rotate-180': !isCollapsed}"
						aria-hidden="true"
						filled
					/>
				</button>
			</div>
		</div>
		<figure v-if="blok.image?.filename?.length" class="max-md:w-full flex justify-center md:w-[18.75rem] flex-none text-center">
			<NuxtImg
				:src="blok.image.filename"
				:alt="blok.imageAlt ?? ''"
				provider="storyblok"
				width="300"
				height="300"
				sizes="sm:100vw md:300px"
				class="rounded-2xl"
			/>
		</figure>
	</section>
</template>

<script setup lang="ts">
	import {useElementSize, useResizeObserver, useToggle} from '@vueuse/core';
	import type {MoreInfoBannerStoryblok, RichtextStoryblok} from '@/storyblok/types';

	interface Props {
		blok: MoreInfoBannerStoryblok
	}

	const props = defineProps<Props>();

	const MAX_CONTENT_HEIGHT = 230;
	const CONTENT_LINE_HEIGHT = 20;
	const CONTENT_BLOCK_GAP = 32;
	const DEFAULT_CHARS_PER_LINE = 72;
	const AVERAGE_CHARACTER_WIDTH = 7.5;
	const ESTIMATE_FALLBACK_RANGE = 45;
	const RICH_TEXT_BLOCK_TYPES = new Set(['paragraph', 'heading', 'blockquote', 'list_item']);

	const contentColumnRef = ref<HTMLElement | null>(null);
	const contentWrapperRef = ref<HTMLElement | null>(null);
	const measuredOverflow = ref<boolean | null>(null);
	const [expanded, toggleExpanded] = useToggle(false);

	const {width: contentColumnWidth} = useElementSize(contentColumnRef);

	const contentBlocks = computed(() => getRichTextBlocks(props.blok.content));
	const contentCharacterCount = computed(() => contentBlocks.value.reduce((total, block) => total + block, 0));
	const charsPerLine = computed(() => {
		if (!contentColumnWidth.value) {
			return DEFAULT_CHARS_PER_LINE;
		}

		return Math.max(1, Math.floor(contentColumnWidth.value / AVERAGE_CHARACTER_WIDTH));
	});

	const estimatedContentHeight = computed(() => (
		contentBlocks.value.reduce((total, characters) => {
			const lineCount = Math.max(1, Math.ceil(characters / charsPerLine.value));
			return total + lineCount * CONTENT_LINE_HEIGHT;
		}, 0) + Math.max(0, contentBlocks.value.length - 1) * CONTENT_BLOCK_GAP
	));

	const estimatedOverflow = computed(() => estimatedContentHeight.value > MAX_CONTENT_HEIGHT);
	const shouldUseMeasuredFallback = computed(() => (
		contentCharacterCount.value === 0
		|| Math.abs(estimatedContentHeight.value - MAX_CONTENT_HEIGHT) <= ESTIMATE_FALLBACK_RANGE
	));

	const isOverflowing = computed(() => {
		if (shouldUseMeasuredFallback.value && measuredOverflow.value !== null) {
			return measuredOverflow.value;
		}

		return estimatedOverflow.value;
	});

	const isCollapsed = computed(() => isOverflowing.value && !expanded.value);

	function checkOverflow() {
		if (!contentWrapperRef.value) {
			return;
		}

		measuredOverflow.value = contentWrapperRef.value.scrollHeight > MAX_CONTENT_HEIGHT;
	}

	function getRichTextBlocks(node?: RichtextStoryblok): number[] {
		if (!node) {
			return [];
		}

		if (RICH_TEXT_BLOCK_TYPES.has(node.type)) {
			return [getRichTextCharacterCount(node)];
		}

		return node.content?.flatMap(getRichTextBlocks) ?? [];
	}

	function getRichTextCharacterCount(node?: RichtextStoryblok): number {
		if (!node) {
			return 0;
		}

		return (node.text?.length ?? 0) + (node.content?.reduce((total, child) => total + getRichTextCharacterCount(child), 0) ?? 0);
	}

	onMounted(checkOverflow);

	useResizeObserver(contentWrapperRef, checkOverflow);
</script>
